/**
 * Draw button + tool picker, in the chart action bar.
 *
 * Follows CleanToggle exactly: the main button is a one-click toggle (arms the
 * last tool you used, presses again to go back to select mode) and the caret
 * opens the picker. Drawing is something you turn on and off constantly, so
 * putting the common action behind a menu would cost a click every time.
 *
 * Styling reuses the levels-* classes the same way CleanToggle does — this is
 * the same kind of control and should be indistinguishable from its neighbours.
 * The LevelsToggle.css import is load-bearing: the classes are global once
 * imported, but do not assume another component already pulled it in.
 */
import React, { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { PenLine, X, ChevronDown, Trash2 } from 'lucide-react';
import { useDrawingStore } from '../store/drawingStore';
import { TOOL_LABELS, ANCHOR_COUNT, type DrawingTool } from './Chart/drawingGeometry';
import './LevelsToggle.css';
import './CleanToggle.css';

const TOOLS: DrawingTool[] = ['line', 'ray', 'horizontal', 'channel'];

const TOOL_HINTS: Record<DrawingTool, string> = {
  line: 'Segment between two points',
  ray: 'Starts at a point, extends to the right edge',
  horizontal: 'Price level across the whole chart',
  channel: 'Two parallel rails — third click sets the width',
};

interface DrawingToggleProps {
  symbol: string;
}

const DrawingToggle: React.FC<DrawingToggleProps> = ({ symbol }) => {
  const activeTool = useDrawingStore((s) => s.activeTool);
  const setActiveTool = useDrawingStore((s) => s.setActiveTool);
  const selectedId = useDrawingStore((s) => s.selectedId);
  const pending = useDrawingStore((s) => s.pending);
  const remove = useDrawingStore((s) => s.remove);
  const clearSymbol = useDrawingStore((s) => s.clearSymbol);
  const count = useDrawingStore((s) => (s.bySymbol[symbol] ?? []).length);

  // Remembered so the main button can re-arm what you were last drawing rather
  // than always defaulting to 'line'.
  const lastTool = useRef<DrawingTool>('line');
  useEffect(() => {
    if (activeTool) lastTool.current = activeTool;
  }, [activeTool]);

  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const handleToggleExpand = useCallback(() => {
    if (!isExpanded && containerRef.current) {
      // Measured off the real button rect: the dropdown is portalled to body,
      // because inside the action bar it would be clipped by the chart.
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left });
    }
    setIsExpanded((v) => !v);
  }, [isExpanded]);

  const handleClose = useCallback(() => setIsExpanded(false), []);

  // The button is the last item of the action bar, so a dropdown anchored at
  // its left edge runs off the right side of the viewport. Once it has rendered
  // and has a real width, pull it back inside the screen.
  useLayoutEffect(() => {
    if (!isExpanded || !dropdownRef.current) return;
    const width = dropdownRef.current.offsetWidth;
    const maxLeft = window.innerWidth - width - 8;
    setDropdownPos((pos) => {
      const left = Math.max(8, Math.min(pos.left, maxLeft));
      return left === pos.left ? pos : { ...pos, left };
    });
  }, [isExpanded]);

  useEffect(() => {
    if (!isExpanded) return;
    const handleClickOutside = (e: MouseEvent): void => {
      const target = e.target as Node;
      if (!containerRef.current?.contains(target) && !dropdownRef.current?.contains(target)) {
        setIsExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded]);

  const need = activeTool ? ANCHOR_COUNT[activeTool] - pending.length : 0;

  return (
    <div ref={containerRef} className="clean-toggle-group">
      <button
        className={`action-bar-btn ${activeTool ? 'active' : ''}`}
        onClick={() => setActiveTool(activeTool ? null : lastTool.current)}
        title={
          activeTool
            ? `Drawing ${TOOL_LABELS[activeTool]} — ${need} more click${need === 1 ? '' : 's'} (Esc to cancel)`
            : 'Draw on the chart'
        }
      >
        <PenLine size={14} className="action-bar-icon" />
        <span className="action-bar-label">
          {activeTool ? TOOL_LABELS[activeTool] : 'Draw'}
        </span>
      </button>
      <button
        className="action-bar-btn clean-config-btn"
        onClick={handleToggleExpand}
        title={`Pick a tool (${count} drawing${count === 1 ? '' : 's'} on ${symbol})`}
      >
        <ChevronDown size={12} className="action-bar-icon" />
      </button>

      {isExpanded &&
        createPortal(
          <div
            ref={dropdownRef}
            className="levels-dropdown"
            // zIndex required, not decorative — .levels-dropdown supplies the
            // background but no stacking order, so without this the chart canvas
            // paints straight over it. Same note as CleanToggle.
            style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, zIndex: 10000 }}
          >
            <div className="levels-dropdown-header">
              <span>Drawing tools</span>
              <button className="levels-close-btn" onClick={handleClose}>
                <X size={14} />
              </button>
            </div>

            <div className="levels-list">
              {TOOLS.map((t) => (
                <div
                  key={t}
                  className={`levels-item ${activeTool === t ? 'active' : ''}`}
                  onClick={() => setActiveTool(activeTool === t ? null : t)}
                  title={TOOL_HINTS[t]}
                >
                  <div className="levels-item-left">
                    <span className="levels-item-label">{TOOL_LABELS[t]}</span>
                    <span className="levels-item-desc">
                      {ANCHOR_COUNT[t]} click{ANCHOR_COUNT[t] === 1 ? '' : 's'} · {TOOL_HINTS[t]}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="levels-toggle-all">
              <button
                className="levels-all-btn"
                disabled={!selectedId}
                onClick={() => selectedId && remove(symbol, selectedId)}
              >
                Delete selected
              </button>
              <button
                className="levels-all-btn"
                disabled={count === 0}
                onClick={() => {
                  // Destructive and not undoable, so it asks. Deleting one
                  // drawing does not — that is a single easy redraw.
                  if (window.confirm(`Clear all ${count} drawings on ${symbol}?`)) {
                    clearSymbol(symbol);
                    setIsExpanded(false);
                  }
                }}
              >
                <Trash2 size={11} /> Clear all
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default DrawingToggle;
